import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CELL_SIZE = 14;
const LABEL_AREA = 120;
const MARGIN_TOP = 10;
const MARGIN_RIGHT = 20;
const MARGIN_BOTTOM = 20;
const CLUSTER_STROKE_WIDTH = 2.5;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  contentRow: {
    display: 'flex',
    gap: '24px',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  svgWrapper: {
    flex: '1 1 auto',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '600px',
  },
  sidePanel: {
    flex: '0 0 260px',
    minWidth: '240px',
  },
  pairSection: {
    marginBottom: '20px',
  },
  pairSectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '8px',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '4px',
  },
  pairItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
    fontSize: '11px',
    color: '#374151',
    borderBottom: '1px solid #f3f4f6',
  },
  pairLabels: {
    flex: '1 1 auto',
    marginRight: '8px',
    lineHeight: '1.3',
  },
  pairScore: {
    flex: '0 0 auto',
    fontWeight: 600,
    fontSize: '12px',
    borderRadius: '4px',
    padding: '2px 6px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '260px',
  },
  legend: {
    marginTop: '12px',
    display: 'flex',
    gap: '12px',
    fontSize: '11px',
    color: '#9ca3af',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  legendSwatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
  viewToggle: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
    alignItems: 'center',
  },
  toggleButton: {
    padding: '4px 12px',
    borderRadius: '4px',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#6b7280',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  toggleButtonActive: {
    padding: '4px 12px',
    borderRadius: '4px',
    border: '1px solid #3b82f6',
    background: '#eff6ff',
    color: '#2563eb',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

/**
 * Aggregate the NxN matrix into a smaller matrix grouped by state or cluster.
 * Returns { labels, matrix } for the aggregated view.
 */
function aggregateByCluster(data) {
  const { labels, matrix, clusters } = data;
  if (!clusters || clusters.length === 0) {
    // Fall back to state-based aggregation
    return aggregateByState(data);
  }

  const aggLabels = clusters.map((c) => c.label || `Cluster ${c.id}`);
  const aggMatrix = [];

  for (let ci = 0; ci < clusters.length; ci++) {
    aggMatrix[ci] = [];
    for (let cj = 0; cj < clusters.length; cj++) {
      const membersI = clusters[ci].members;
      const membersJ = clusters[cj].members;
      let sum = 0;
      let count = 0;
      for (const mi of membersI) {
        for (const mj of membersJ) {
          if (matrix[mi] && matrix[mi][mj] != null) {
            sum += matrix[mi][mj];
            count++;
          }
        }
      }
      aggMatrix[ci][cj] = count > 0 ? sum / count : 0;
    }
  }

  return { labels: aggLabels, matrix: aggMatrix };
}

function aggregateByState(data) {
  const { labels, matrix } = data;
  // Extract state abbreviations from labels (last 2 chars or after last space)
  const stateMap = {};
  labels.forEach((label, idx) => {
    const parts = label.trim().split(/\s+/);
    const state = parts[parts.length - 1] || label;
    if (!stateMap[state]) stateMap[state] = [];
    stateMap[state].push(idx);
  });

  const stateKeys = Object.keys(stateMap).sort();
  const aggLabels = stateKeys;
  const aggMatrix = [];

  for (let si = 0; si < stateKeys.length; si++) {
    aggMatrix[si] = [];
    for (let sj = 0; sj < stateKeys.length; sj++) {
      const idxsI = stateMap[stateKeys[si]];
      const idxsJ = stateMap[stateKeys[sj]];
      let sum = 0;
      let count = 0;
      for (const i of idxsI) {
        for (const j of idxsJ) {
          if (matrix[i] && matrix[i][j] != null) {
            sum += matrix[i][j];
            count++;
          }
        }
      }
      aggMatrix[si][sj] = count > 0 ? sum / count : 0;
    }
  }

  return { labels: aggLabels, matrix: aggMatrix };
}

/**
 * Build a set of cluster boundary segments for drawing on the heatmap.
 * Each boundary is { x, y, width, height } in cell coordinates.
 */
function getClusterBoundaries(clusters, n) {
  if (!clusters || clusters.length === 0) return [];

  // Build a map: index -> clusterId
  const clusterOf = new Array(n).fill(-1);
  clusters.forEach((c) => {
    c.members.forEach((m) => {
      if (m < n) clusterOf[m] = c.id;
    });
  });

  const boundaries = [];
  clusters.forEach((c) => {
    const sorted = [...c.members].filter((m) => m < n).sort((a, b) => a - b);
    if (sorted.length === 0) return;
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    boundaries.push({
      startIdx: min,
      endIdx: max,
      count: max - min + 1,
      label: c.label,
    });
  });

  return boundaries;
}

function getScoreColor(score) {
  if (score >= 0.8) return '#2563eb';
  if (score >= 0.6) return '#60a5fa';
  if (score >= 0.4) return '#e5e7eb';
  if (score >= 0.2) return '#fca5a5';
  return '#dc2626';
}

export default function AddressSimilarityMatrix({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [viewMode, setViewMode] = useState('full'); // 'full' or 'aggregated'

  const isLarge = data && data.labels && data.labels.length > 30;

  useEffect(() => {
    if (!data || !data.labels || !data.matrix || data.labels.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Determine which data to render
    const useAggregated = viewMode === 'aggregated' || (isLarge && viewMode === 'full');
    let renderLabels, renderMatrix;

    if (useAggregated && isLarge) {
      const agg = aggregateByCluster(data);
      renderLabels = agg.labels;
      renderMatrix = agg.matrix;
    } else {
      renderLabels = data.labels;
      renderMatrix = data.matrix;
    }

    const n = renderLabels.length;
    if (n === 0) return;

    // Compute label area based on longest label
    const maxLabelLen = Math.max(...renderLabels.map((l) => l.length));
    const dynamicLabelArea = Math.max(LABEL_AREA, Math.ceil(maxLabelLen * 5.5) + 20);

    const gridSize = n * CELL_SIZE;
    const width = dynamicLabelArea + gridSize + MARGIN_RIGHT;
    const height = MARGIN_TOP + dynamicLabelArea + gridSize + MARGIN_BOTTOM;

    svg.attr('width', width).attr('height', height);

    // Color scale: red (0) -> white (0.5) -> blue (1)
    const colorScale = d3.scaleLinear()
      .domain([0, 0.5, 1])
      .range(['#dc2626', '#ffffff', '#2563eb'])
      .clamp(true);

    const g = svg.append('g');

    const gridX = dynamicLabelArea;
    const gridY = MARGIN_TOP + dynamicLabelArea;

    // Column labels (top, rotated)
    g.selectAll('.col-label')
      .data(renderLabels)
      .enter()
      .append('text')
      .attr('class', 'col-label')
      .attr('x', (d, i) => gridX + i * CELL_SIZE + CELL_SIZE / 2)
      .attr('y', gridY - 6)
      .attr('text-anchor', 'start')
      .attr('transform', (d, i) => {
        const x = gridX + i * CELL_SIZE + CELL_SIZE / 2;
        const y = gridY - 6;
        return `rotate(-55, ${x}, ${y})`;
      })
      .style('font-size', '9px')
      .style('fill', '#6b7280')
      .style('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
      .text((d) => d);

    // Row labels (left)
    g.selectAll('.row-label')
      .data(renderLabels)
      .enter()
      .append('text')
      .attr('class', 'row-label')
      .attr('x', gridX - 6)
      .attr('y', (d, i) => gridY + i * CELL_SIZE + CELL_SIZE / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .style('font-size', '9px')
      .style('fill', '#374151')
      .style('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
      .text((d) => d);

    // Heatmap cells
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        const val = renderMatrix[r] && renderMatrix[r][c] != null ? renderMatrix[r][c] : 0;
        const isDiagonal = r === c;

        g.append('rect')
          .attr('x', gridX + c * CELL_SIZE)
          .attr('y', gridY + r * CELL_SIZE)
          .attr('width', CELL_SIZE - 1)
          .attr('height', CELL_SIZE - 1)
          .attr('fill', isDiagonal ? '#e5e7eb' : colorScale(val))
          .attr('stroke', '#f9fafb')
          .attr('stroke-width', 0.5)
          .attr('rx', 1)
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            setTooltip({
              x: event.clientX + 14,
              y: event.clientY - 14,
              row: renderLabels[r],
              col: renderLabels[c],
              value: val,
              isDiagonal,
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });
      }
    }

    // Cluster boundaries
    if (!useAggregated || !isLarge) {
      const boundaries = getClusterBoundaries(data.clusters, n);
      boundaries.forEach((b) => {
        g.append('rect')
          .attr('x', gridX + b.startIdx * CELL_SIZE - 1)
          .attr('y', gridY + b.startIdx * CELL_SIZE - 1)
          .attr('width', b.count * CELL_SIZE + 1)
          .attr('height', b.count * CELL_SIZE + 1)
          .attr('fill', 'none')
          .attr('stroke', '#1f2937')
          .attr('stroke-width', CLUSTER_STROKE_WIDTH)
          .attr('rx', 2)
          .attr('pointer-events', 'none');
      });
    }

    // Color legend bar
    const legendX = gridX;
    const legendY = gridY + gridSize + 16;
    const legendWidth = Math.min(200, gridSize);
    const legendHeight = 10;

    const defs = svg.append('defs');
    const gradientId = 'similarity-gradient';
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('x2', '100%');

    gradient.append('stop').attr('offset', '0%').attr('stop-color', '#dc2626');
    gradient.append('stop').attr('offset', '50%').attr('stop-color', '#ffffff');
    gradient.append('stop').attr('offset', '100%').attr('stop-color', '#2563eb');

    g.append('rect')
      .attr('x', legendX)
      .attr('y', legendY)
      .attr('width', legendWidth)
      .attr('height', legendHeight)
      .attr('fill', `url(#${gradientId})`)
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 0.5)
      .attr('rx', 2);

    g.append('text')
      .attr('x', legendX)
      .attr('y', legendY + legendHeight + 12)
      .style('font-size', '9px')
      .style('fill', '#9ca3af')
      .text('0 (dissimilar)');

    g.append('text')
      .attr('x', legendX + legendWidth)
      .attr('y', legendY + legendHeight + 12)
      .attr('text-anchor', 'end')
      .style('font-size', '9px')
      .style('fill', '#9ca3af')
      .text('1 (identical)');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, viewMode, isLarge]);

  // Empty state
  if (!data || !data.labels || !data.matrix || data.labels.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Address Similarity Matrix</div>
        <div style={styles.subtitle}>Pairwise pricing profile similarity across all addresses</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No similarity data available
        </div>
      </div>
    );
  }

  const { topPairs, bottomPairs } = data;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Address Similarity Matrix</div>
      <div style={styles.subtitle}>Pairwise pricing profile similarity across all addresses</div>

      {/* View mode toggle for large matrices */}
      {isLarge && (
        <div style={styles.viewToggle}>
          <span style={{ fontSize: '12px', color: '#6b7280', marginRight: '4px' }}>View:</span>
          <button
            style={viewMode === 'full' ? styles.toggleButtonActive : styles.toggleButton}
            onClick={() => setViewMode('full')}
          >
            Aggregated
          </button>
          <button
            style={viewMode === 'detailed' ? styles.toggleButtonActive : styles.toggleButton}
            onClick={() => setViewMode('detailed')}
          >
            Full Detail
          </button>
          {viewMode === 'full' && (
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
              Showing grouped view ({data.labels.length} addresses)
            </span>
          )}
        </div>
      )}

      <div style={styles.contentRow}>
        {/* SVG Heatmap */}
        <div style={styles.svgWrapper}>
          <svg ref={svgRef} />
        </div>

        {/* Side panel: top similar / most dissimilar pairs */}
        {(topPairs || bottomPairs) && (
          <div style={styles.sidePanel}>
            {topPairs && topPairs.length > 0 && (
              <div style={styles.pairSection}>
                <div style={styles.pairSectionTitle}>Most Similar Pairs</div>
                {topPairs.map((pair, idx) => (
                  <div key={`top-${idx}`} style={styles.pairItem}>
                    <div style={styles.pairLabels}>
                      <div style={{ fontWeight: 500 }}>{pair.label1}</div>
                      <div style={{ color: '#9ca3af' }}>{pair.label2}</div>
                    </div>
                    <span
                      style={{
                        ...styles.pairScore,
                        background: '#eff6ff',
                        color: '#2563eb',
                      }}
                    >
                      {pair.similarity.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {bottomPairs && bottomPairs.length > 0 && (
              <div style={styles.pairSection}>
                <div style={styles.pairSectionTitle}>Most Dissimilar Pairs</div>
                {bottomPairs.map((pair, idx) => (
                  <div key={`bottom-${idx}`} style={styles.pairItem}>
                    <div style={styles.pairLabels}>
                      <div style={{ fontWeight: 500 }}>{pair.label1}</div>
                      <div style={{ color: '#9ca3af' }}>{pair.label2}</div>
                    </div>
                    <span
                      style={{
                        ...styles.pairScore,
                        background: '#fef2f2',
                        color: '#dc2626',
                      }}
                    >
                      {pair.similarity.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.isDiagonal ? tooltip.row : `${tooltip.row} vs ${tooltip.col}`}
          </div>
          <div style={{ color: '#374151' }}>
            Similarity:{' '}
            <strong style={{ color: getScoreColor(tooltip.value) }}>
              {tooltip.value.toFixed(3)}
            </strong>
          </div>
          {tooltip.isDiagonal && (
            <div style={{ color: '#9ca3af', fontStyle: 'italic', marginTop: '2px', fontSize: '11px' }}>
              Self-comparison (always 1.0)
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div style={styles.legend}>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#dc2626' }} />
          Low similarity
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#fff', border: '1px solid #e5e7eb' }} />
          Mid (0.5)
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#2563eb' }} />
          High similarity
        </span>
        {data.clusters && data.clusters.length > 0 && (
          <span>
            <span
              style={{
                ...styles.legendSwatch,
                background: 'transparent',
                border: '2px solid #1f2937',
              }}
            />
            Cluster boundary
          </span>
        )}
      </div>
    </div>
  );
}
